import React, { useState, useEffect, useCallback } from 'react';

const WORKER_URL = 'https://rakeen-whatsapp-poc.thamer-21d.workers.dev';

const STATUS_FLOW = ['inquiry', 'quoted', 'confirmed', 'processing', 'ready', 'delivered'];
const STATUS_LABELS = {
  inquiry: 'استفسار',
  quoted: 'عرض سعر',
  confirmed: 'تأكيد',
  processing: 'تجهيز',
  ready: 'جاهز',
  delivered: 'تسليم',
};

const STATUS_COLORS = {
  inquiry: '#C8A960',
  quoted: '#60A5FA',
  confirmed: '#34D399',
  processing: '#F59E0B',
  ready: '#8B5CF6',
  delivered: '#6B7280',
};

function OrdersPage({ idToken, onNavigate }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('pipeline'); // 'list' or 'pipeline'
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const fetchOrders = useCallback(async () => {
    try {
      const res = await fetch(`${WORKER_URL}/api/conversations`, {
        cache: 'no-store',
        headers: { 'Authorization': `Bearer ${idToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(data.filter((o) => !o.isTest));
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 10000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const advanceOrder = async (orderId) => {
    await fetch(`${WORKER_URL}/api/orders/${orderId}/advance`, { method: 'POST' });
    await fetchOrders();
  };

  // Filter + search
  let filtered = orders;
  if (statusFilter !== 'all') filtered = filtered.filter((o) => o.status === statusFilter);
  if (search.trim()) {
    const q = search.trim().toLowerCase();
    filtered = filtered.filter((o) =>
      o.phone.includes(q) || o.id.toLowerCase().includes(q) || (o.product && o.product.includes(q))
    );
  }

  if (loading) {
    return <div className="sd-orders-loading">جاري التحميل...</div>;
  }

  return (
    <div className="sd-orders">
      {/* Controls */}
      <div className="sd-orders-controls">
        <div className="sd-orders-view-toggle">
          <button
            className={`sd-orders-view-btn ${view === 'pipeline' ? 'active' : ''}`}
            onClick={() => setView('pipeline')}
          >
            🔀 خط الإنتاج
          </button>
          <button
            className={`sd-orders-view-btn ${view === 'list' ? 'active' : ''}`}
            onClick={() => setView('list')}
          >
            📄 قائمة
          </button>
        </div>

        <div className="sd-orders-tools">
          <input
            type="text"
            className="sd-orders-search"
            placeholder="🔍 بحث..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            dir="rtl"
          />
          {view === 'list' && (
            <select
              className="sd-orders-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">الكل ({orders.length})</option>
              {STATUS_FLOW.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]} ({orders.filter((o) => o.status === s).length})
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Pipeline View */}
      {view === 'pipeline' && (
        <div className="sd-pipeline">
          <div className="sd-pipeline-columns">
            {STATUS_FLOW.map((status) => {
              const columnOrders = (search.trim()
                ? filtered
                : orders
              ).filter((o) => o.status === status);

              return (
                <div key={status} className="sd-pipeline-column">
                  <div className="sd-pipeline-column-header">
                    <span className="sd-pipeline-column-dot" style={{ background: STATUS_COLORS[status] }} />
                    <span className="sd-pipeline-column-title">{STATUS_LABELS[status]}</span>
                    <span className="sd-pipeline-column-count">{columnOrders.length}</span>
                  </div>
                  <div className="sd-pipeline-cards">
                    {columnOrders.map((order) => (
                      <div
                        key={order.id}
                        className="sd-pipeline-card"
                        onClick={() => onNavigate('conversations')}
                      >
                        <div className="sd-pipeline-card-id">#{order.id}</div>
                        <div className="sd-pipeline-card-product">{order.product || '—'}</div>
                        <div className="sd-pipeline-card-phone">{order.phone}</div>
                        {order.status !== 'delivered' && (
                          <button
                            className="sd-pipeline-card-advance"
                            onClick={(e) => { e.stopPropagation(); advanceOrder(order.id); }}
                          >
                            ⬅ {STATUS_LABELS[STATUS_FLOW[STATUS_FLOW.indexOf(status) + 1]] || ''}
                          </button>
                        )}
                      </div>
                    ))}
                    {columnOrders.length === 0 && (
                      <div className="sd-pipeline-empty">—</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* List View */}
      {view === 'list' && (
        <div className="sd-orders-list">
          {filtered.length === 0 ? (
            <div className="sd-orders-empty">لا توجد طلبات</div>
          ) : (
            <div className="sd-orders-table">
              <div className="sd-orders-table-header">
                <span>الرقم</span>
                <span>المنتج</span>
                <span>التفاصيل</span>
                <span>الحالة</span>
                <span>الهاتف</span>
                <span>التاريخ</span>
              </div>
              {filtered.map((order) => (
                <div
                  key={order.id}
                  className="sd-orders-table-row"
                  onClick={() => onNavigate('conversations')}
                >
                  <span className="sd-orders-cell-id">#{order.id}</span>
                  <span className="sd-orders-cell-product">{order.product || '—'}</span>
                  <span className="sd-orders-cell-specs">{order.specs || '—'}</span>
                  <span className={`sd-orders-cell-status sd-status-${order.status}`}>
                    {STATUS_LABELS[order.status] || order.status}
                  </span>
                  <span className="sd-orders-cell-phone">{order.phone}</span>
                  <span className="sd-orders-cell-date">
                    {order.createdAt ? new Date(order.createdAt).toLocaleDateString('ar-SA') : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default OrdersPage;
